import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ShopSection.css';
import rice from '../../assets/rice-10kgs.jpg';
import cola from '../../assets/cola.jpg';
import wipes from '../../assets/wipe.jpg';
import shampoo from '../../assets/shampoo.jpg';
import crisp from '../../assets/crisp.jpg';
import teabag from '../../assets/teabag.jpg';
import toilet from '../../assets/toilet.jpg';

// Static bulk products for Bulk Bargains UK with updated image URLs
const staticProducts = [
  {
    id: '1',
    name: 'Bulk Rice (10kg)',
    category: 'Groceries',
    images: [rice], // Bulk rice sack
    price: 15,
    bulkOptions: { '5kg': 8, '10kg': 15, '20kg': 28 },
    description: 'Quality rice perfect for bulk buys.',
  },
  {
    id: '2',
    name: 'Cola Crate (24-pack)',
    category: 'Beverages',
    images: [cola], // Cola cans in bulk
    price: 10,
    bulkOptions: { '12-pack': 6, '24-pack': 10, '48-pack': 18 },
    description: 'Fizzy cola in bulk for your trade.',
  },
  {
    id: '3',
    name: 'Cleaning Wipes (100-pack)',
    category: 'Household Goods',
    images: [wipes], // Pack of cleaning wipes
    price: 8,
    bulkOptions: { '50-pack': 5, '100-pack': 8, '200-pack': 15 },
    description: 'Wipes for bulk cleaning needs.',
  },
  {
    id: '4',
    name: 'Crisps Bulk Box (48-pack)',
    category: 'Snacks & Treats',
    images: [crisp], // Bulk crisps packs
    price: 12,
    bulkOptions: { '24-pack': 7, '48-pack': 12, '96-pack': 22 },
    description: 'Tasty crisps in bulk packs.',
  },
  {
    id: '5',
    name: 'Frozen Pizzas (10-pack)',
    category: 'Frozen Foods',
    images: ['https://images.unsplash.com/photo-1595854341625-f33ee10dbf94?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'], // Stack of frozen pizzas
    price: 25,
    bulkOptions: { '5-pack': 14, '10-pack': 25, '20-pack': 45 },
    description: 'Bulk pizzas for quick meals.',
  },
  {
    id: '6',
    name: 'Shampoo Bottles (12-pack)',
    category: 'Personal Care',
    images: [shampoo], // Shampoo bottles
    price: 18,
    bulkOptions: { '6-pack': 10, '12-pack': 18, '24-pack': 34 },
    description: 'Moisturising shampoo in bulk.',
  },
  {
    id: '7',
    name: 'Tea Bags (240-pack)',
    category: 'Beverages',
    images: [teabag], // Bulk tea bags
    price: 9,
    bulkOptions: { '120-pack': 5, '240-pack': 9, '480-pack': 16 },
    description: 'Proper tea in bulk packs.',
  },
  {
    id: '8',
    name: 'Toilet Rolls (36-pack)',
    category: 'Household Goods',
    images: [toilet], // Bulk toilet rolls
    price: 14,
    bulkOptions: { '18-pack': 8, '36-pack': 14, '72-pack': 26 },
    description: 'Bulk toilet rolls for big savings.',
  },
];

const ShopSection = ({ addToCart }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const productsPerPage = 8;
  const productsGridRef = useRef(null);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = staticProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  const totalPages = Math.ceil(staticProducts.length / productsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    productsGridRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="shop-section" id="shop">
      <h2 ref={productsGridRef}>Bulk Bargains Marketplace</h2>
      <div className="products-grid">
        {currentProducts.map((product) => (
          <motion.div
            key={product.id}
            className="product-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            onClick={() => setSelectedProduct(product)}
          >
            <img
              src={product.images[0]}
              alt={product.name}
              className="product-image"
              onError={(e) => {
                console.error(`Failed to load image: ${product.images[0]}`);
                e.target.src = 'https://via.placeholder.com/280x200?text=Image+Not+Found';
              }}
            />
            {product.price < 10 && <div className="deal-particle" style={{ top: '10%', left: '80%' }} />}
            <div className="product-details">
              <h3>{product.name}</h3>
              <p className="product-description">{product.description}</p>
              <div className="bulk-pricing">
                {Object.entries(product.bulkOptions).map(([qty, price]) => (
                  <div key={qty} className="bulk-option">
                    <span>{qty}: £{price}</span>
                    <select
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => addToCart(product, qty, price)}
                    >
                      <option value="0">Add</option>
                      {[...Array(5)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {staticProducts.length > productsPerPage && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => paginate(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
      <AnimatePresence>
        {selectedProduct && (
          <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} addToCart={addToCart} />
        )}
      </AnimatePresence>
    </section>
  );
};

const ProductModal = ({ product, onClose, addToCart }) => {
  const [rotation, setRotation] = useState(0);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 10;
    setRotation(x);
  };

  return (
    <motion.div
      className="product-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <motion.div
        className="product-modal"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close modal">
          ×
        </button>
        <div className="modal-content">
          <div className="modal-3d-stack" onMouseMove={handleMouseMove}>
            <img
              src={product.images[0]}
              alt={product.name}
              style={{ transform: `rotateY(${rotation}deg)` }}
              onError={(e) => {
                console.error(`Failed to load image: ${product.images[0]}`);
                e.target.src = 'https://via.placeholder.com/700x300?text=Image+Not+Found';
              }}
            />
          </div>
          <h3>{product.name}</h3>
          <p className="modal-description">{product.description}</p>
          <div className="bulk-pricing">
            {Object.entries(product.bulkOptions).map(([qty, price]) => (
              <div key={qty} className="bulk-option">
                <span>{qty}: £{price}</span>
                <select onChange={(e) => addToCart(product, qty, price)}>
                  <option value="0">Add to Cart</option>
                  {[...Array(10)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ShopSection;